// Server configuration utility for BragaBJJ API endpoints
// Handles proper server URL configuration for local development and production

/**
 * Get the correct server URL based on environment and access method
 * Detects tunnel access vs local development
 * Defaults to localhost:50008 for local development
 */
export const getServerUrl = () => {
  // Detect if we're accessing via the tunnel domain
  const isOnTunnel = typeof window !== 'undefined' && window.location.hostname === 'braga.maplusvision.com';
  
  if (isOnTunnel) {
    return 'https://braga.maplusvision.com';  // Use HTTPS tunnel for public access
  }
  
  // prefer explicit env override
  const env = process.env.REACT_APP_SERVER_URL;
  // allow overriding only the API port (useful when frontend is served on a different port)
  const apiPort = process.env.REACT_APP_API_PORT || '50008';

  // If an explicit server URL is provided via env, use it
  if (env) return env;

  // Construct a default base using the current page host and chosen API port.
  // This helps when the frontend is served on a different port (for example 50001)
  try {
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
      const host = window.location.hostname || 'localhost';
      
      // CRITICAL FIX: Always use HTTP for localhost/127.0.0.1, even if frontend is HTTPS
      // The backend server.js runs HTTP only on port 50008
      if (host === 'localhost' || host === '127.0.0.1') {
        return `http://${host}:${apiPort}`;
      }
      
      // For non-localhost (tunnel domains), use HTTPS
      const proto = window.location.protocol || 'http:';
      return `${proto}//${host}:${apiPort}`;
    }
  } catch (e) {
    // fallback to localhost
  }

  // Default to HTTP for localhost
  return `http://localhost:${apiPort}`;
};

// Remember a working API base (http/https + host:port) once discovered
let _cachedApiBase = null;

export const _getCachedApiBase = () => _cachedApiBase;


/**
 * Create API endpoint URL with proper server base
 * @param {string} endpoint - API endpoint path (e.g., '/api/data')
 * @returns {string} Full URL to the API endpoint
 */
export const getApiUrl = (endpoint) => {
  const serverUrl = _cachedApiBase || getServerUrl();
  // Ensure endpoint starts with /
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${serverUrl}${path}`;
};

/**
 * Enhanced fetch wrapper that automatically uses correct server URL
 * @param {string} endpoint - API endpoint path
 * @param {object} options - Fetch options
 * @returns {Promise} Fetch promise
 */
export const apiFetch = async (endpoint, options = {}) => {
  const url = getApiUrl(endpoint);
  
  try {
    // debug: show the resolved URL and options (lightweight)
    if (typeof console !== 'undefined' && console.debug) {
      try { console.debug('[apiFetch] ->', url, options && options.method ? options.method : 'GET'); } catch (e) {}
    }
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });
    // if successful, remember the origin as a working base for future calls
    try {
      const origin = new URL(url).origin;
      _cachedApiBase = origin;
    } catch (e) {}

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    return response;
  } catch (error) {
    // include more runtime hints for network failures (CORS/mixed-content/backends not running)
    try { console.error(`❌ API request to ${url} failed:`, error); } catch (e) {}

    // Build helpful hints for common dev issues
    const hints = [];
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) hints.push('Your browser reports offline (navigator.onLine === false).');
      if (typeof window !== 'undefined' && window.location) {
        const pageProto = window.location.protocol;
        if (pageProto === 'https:' && url.startsWith('http://')) {
          hints.push('Mixed-content detected: the page is served over HTTPS but the backend URL is HTTP. Browsers block insecure requests from secure pages.');
          hints.push('Solutions: run the backend with HTTPS, set REACT_APP_SERVER_URL to an HTTPS backend, or serve the frontend over HTTP in development.');
        }
        if ((url.startsWith('https://localhost') || url.startsWith('https://127.0.0.1')) && pageProto === 'http:') {
          hints.push('You are fetching an HTTPS localhost backend while the page is HTTP — ensure the backend certificate is trusted by the browser.');
        }
      }
    } catch (e) {/* ignore */}

    // If the failure was to an https://localhost address and the page itself is NOT https,
    // attempt a safe retry over plain http in case the backend is running only on http.
    try {
      // If the failure was to an https://localhost address and the page itself is NOT https,
      // attempt a safe retry over plain http in case the backend is running only on http.
      if (typeof window !== 'undefined' && window.location && window.location.protocol !== 'https:' && url.startsWith('https://localhost')) {
        const fallback = url.replace('https://', 'http://');
        try { console.warn('[apiFetch] Retry: attempting fallback to', fallback); } catch (e) {}
        const fallbackRes = await fetch(fallback, {
          headers: {
            'Content-Type': 'application/json',
            ...options.headers
          },
          ...options
        });
        if (fallbackRes.ok) {
          try { _cachedApiBase = new URL(fallback).origin; } catch (e) {}
          return fallbackRes;
        }
      }
    } catch (e) {
      // fallback attempt failed — continue to throw original error with hints
      try { console.debug('[apiFetch] Fallback attempt also failed:', e); } catch (er) {}
    }

    if (hints.length) {
      try { console.warn('[apiFetch] HINTS:'); } catch (e) {}
      hints.forEach(h => { try { console.warn(' -', h); } catch (e) {} });
    }

    // throw a clearer Error including any hints to help the developer debug
    const hintText = hints.length ? '\nHints:\n' + hints.map((h,i)=>`${i+1}. ${h}`).join('\n') : '';
    const outErr = new Error(`${error && error.message ? error.message : 'Network error'} when fetching ${url}${hintText}`);
    outErr.original = error;
    throw outErr;
  }
};

/**
 * Sync data to server using the correct sync endpoint
 * @param {object} data - Data to sync (students, attendance, etc.)
 * @returns {Promise} Sync result
 */
export const syncDataToServer = async (data) => {
  try {
    const response = await apiFetch('/api/sync/push', {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        timestamp: Date.now()
      })
    });
    
    const result = await response.json();
    console.log('✅ Data synced to server successfully:', result);
    return result;
  } catch (error) {
    console.warn('❌ Could not sync to server, will retry on next change:', error);
    throw error;
  }
};

/**
 * Load data from server
 * @returns {Promise<object>} Server data (students, attendance, etc.)
 */
export const loadDataFromServer = async () => {
  try {
    const response = await apiFetch('/api/data');
    const data = await response.json();
    console.log('✅ Loaded data from server:', data);
    return data;
  } catch (error) {
    console.warn('❌ Failed to load from server (primary):', error);
    // Attempt fallbacks: try common localhost bases to work around mixed-content / port mismatches
    const fallbackPorts = [];
    try {
      const apiPort = process.env.REACT_APP_API_PORT || '50008';
      fallbackPorts.push(`http://localhost:${apiPort}`);
      fallbackPorts.push(`http://127.0.0.1:${apiPort}`);
      // If the page is not https, also try https localhost in case backend is https
      if (typeof window !== 'undefined' && window.location && window.location.protocol !== 'https:') {
        fallbackPorts.push(`https://localhost:${apiPort}`);
        fallbackPorts.push(`https://127.0.0.1:${apiPort}`);
      }
    } catch (e) { /* ignore */ }

    for (const base of fallbackPorts) {
      try {
        console.debug('[loadDataFromServer] trying fallback base:', base);
        const res = await forceApiFetch('/api/data', base);
        if (res && res.json) {
          const d = await res.json().catch(() => null);
          if (d) {
            console.log('✅ Loaded data from server (fallback):', base, d);
            return d;
          }
        }
      } catch (e) {
        console.debug('[loadDataFromServer] fallback failed for', base, e && e.message);
        continue;
      }
    }

    console.warn('❌ All loadDataFromServer attempts failed');
    throw error;
  }
};

/**
 * Force a fetch against a specific base URL (useful for dev when you want
 * to target a backend running on a known port regardless of page origin).
 * @param {string} endpoint - API path (e.g. '/api/data')
 * @param {string} baseUrl - Full base URL including protocol and host (e.g. 'http://localhost:50008')
 * @param {object} options - Fetch options
 */
export const forceApiFetch = async (endpoint, baseUrl, options = {}) => {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${baseUrl.replace(/\/$/, '')}${path}`;
  try {
    if (typeof console !== 'undefined' && console.debug) console.debug('[forceApiFetch] ->', url, options && options.method ? options.method : 'GET');
    const res = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });
    if (!res.ok) throw new Error(`Forced API request failed: ${res.status} ${res.statusText}`);
    try { _cachedApiBase = new URL(baseUrl).origin; } catch (e) {}
    return res;
  } catch (err) {
    try { console.error('[forceApiFetch] Error fetching', url, err); } catch (e) {}
    throw err;
  }
};

/**
 * Force-load the aggregated /api/data endpoint from a specific base URL
 */
export const forceLoadDataFromServer = async (baseUrl) => {
  const res = await forceApiFetch('/api/data', baseUrl);
  return await res.json();
};

// Named object for default export (avoids anonymous default export ESLint rule)
const serverUtils = {
  getServerUrl,
  getApiUrl,
  apiFetch,
  syncDataToServer,
  loadDataFromServer
};

export default serverUtils;